/**
 * The payout queue.
 *
 * Real money leaves the business here, so the screen is built around the two
 * things the person paying actually needs: who they are about to pay, and
 * whether anything about the request looks off. Marking a payout paid is the
 * point of no return — it moves the money out of reserved for good — so it
 * asks for the PayPal reference rather than taking a single click.
 */
import { useCallback, useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { BackOfficeNav } from "../components/BackOfficeNav";
import { Meter, MeterRow } from "../components/Meter";
import { Pagination } from "../components/Pagination";
import { RefreshButton } from "../components/RefreshButton";
import { useRefreshOnFocus } from "../hooks/useRefreshOnFocus";
import { ReviewSignIn } from "../components/ReviewSignIn";
import { useToast } from "../hooks/useToast";
import { payoutAdmin, type ReviewCreds } from "../api/endpoints";
import { clearReviewCredentials, readReviewCredentials } from "../lib/session";
import { isCredentialFailure } from "../lib/backOffice";
import { formatDate } from "../lib/validation";
import type {
  AdminPayoutList,
  AdminPayoutRow,
  PayoutDestination,
  PayoutDetails,
  PayoutStatus,
} from "../types/api";

const FILTERS: Array<{ id: PayoutStatus | "all"; label: string }> = [
  { id: "pending", label: "To pay" },
  { id: "processing", label: "In progress" },
  { id: "paid", label: "Paid" },
  { id: "failed", label: "Failed" },
  { id: "all", label: "All" },
];

export function AdminPayouts() {
  const toast = useToast();
  const [creds, setCreds] = useState<ReviewCreds | null>(() => readReviewCredentials());
  const [data, setData] = useState<AdminPayoutList | null>(null);
  const [filter, setFilter] = useState<PayoutStatus | "all">("pending");
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // The chips are not the only thing that changes the filter — the
  // "payouts failed" callout does too, and a page reset wired only into
  // the chips leaves that path on a page the new filter may not have.
  function changeFilter(next: PayoutStatus | "all") {
    setFilter(next);
    setPage(1);
  }

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!creds) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadError(null);
        const result = await payoutAdmin.queue(creds, {
          status: filter === "all" ? undefined : filter,
          page,
        });
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        toast.show((err as Error).message, "error");
        // A rejected key means sign in again; anything else is transient and
        // must not throw finance out of the queue.
        if (isCredentialFailure(err)) {
          clearReviewCredentials();
          setCreds(null);
        } else {
          setLoadError((err as Error).message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creds, filter, nonce, page, toast]);

  useRefreshOnFocus(reload, { enabled: busy === null });

  async function move(
    payoutId: string,
    status: PayoutStatus,
    extra: { provider_reference?: string; failure_reason?: string } = {},
  ) {
    if (!creds) return;
    setBusy(payoutId);
    try {
      await payoutAdmin.setStatus(creds, payoutId, { status, ...extra });
      toast.show(
        status === "paid"
          ? "Marked paid."
          : status === "failed"
            ? "Marked failed — the balance is back with the contributor."
            : "Moved to in progress.",
        "success",
      );
      reload();
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setBusy(null);
    }
  }

  if (!creds) return <ReviewSignIn onSubmit={setCreds} />;

  return (
    <Layout showNav={false} showAccount={false} title="Payouts">
      <section className="section">
        <div className="wrap">
          <BackOfficeNav />

          <div className="row-between">
            <div className="eyebrow">Payouts</div>
            <RefreshButton onClick={reload} busy={loading} />
          </div>
          <h1 className="display" style={{ fontSize: "clamp(22px,4vw,32px)", margin: "0 0 18px" }}>
            {data ? `${data.summary.pending_count} to pay` : "Payouts"}
          </h1>

          <MeterRow>
            <Meter
              label="Owed now"
              value={data && `$${data.summary.pending_total.formatted}`}
            />
            <Meter
              label="In progress"
              value={data && `$${data.summary.processing_total.formatted}`}
            />
            <Meter
              label="Paid to date"
              value={data && `$${data.summary.paid_total.formatted}`}
            />
          </MeterRow>

          {(data?.summary.failed_count ?? 0) > 0 && filter !== "failed" && (
            <button
              type="button"
              className="rerecord-callout"
              onClick={() => changeFilter("failed")}
            >
              <b>
                {data?.summary.failed_count} payout
                {data?.summary.failed_count === 1 ? "" : "s"} failed
              </b>
              <span>The money went back to the contributor — tap to see them.</span>
            </button>
          )}

          <div className="filter-row mt-24">
            {FILTERS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`chip ${filter === entry.id ? "chip-active" : ""}`}
                onClick={() => changeFilter(entry.id)}
              >
                {entry.label}
              </button>
            ))}
          </div>

          {loading && !data ? (
            <div className="panel empty mt-16">
              <div style={{ display: "grid", placeItems: "center", gap: 12 }}>
                <div className="spinner" />
                <div className="muted" style={{ fontSize: 13 }}>
                  Loading the payout queue…
                </div>
              </div>
            </div>
          ) : loadError ? (
            <div className="panel empty mt-16">
              <h3>Couldn't load the queue</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>{loadError}</p>
              <button type="button" className="btn btn-ghost mt-16" onClick={reload}>
                Try again
              </button>
            </div>
          ) : data && data.payouts.length === 0 ? (
            <div className="panel empty mt-16">
              <h3>Nothing here</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                {filter === "pending"
                  ? "No payouts are waiting. Contributors appear here once they redeem."
                  : "Nothing with that status."}
              </p>
            </div>
          ) : (
            <div className="stack mt-16">
              {data?.payouts.map((row) => (
                <PayoutRow
                  key={row.payout.payout_id}
                  row={row}
                  creds={creds}
                  busy={busy === row.payout.payout_id}
                  onMove={move}
                />
              ))}
            </div>
          )}

          {data && (
            <Pagination
              page={data.page}
              hasMore={data.has_more}
              total={data.total}
              noun={data.total === 1 ? "payout" : "payouts"}
              busy={loading}
              onChange={setPage}
            />
          )}
        </div>
      </section>
    </Layout>
  );
}

function PayoutRow({
  row,
  creds,
  busy,
  onMove,
}: {
  row: AdminPayoutRow;
  // Passed down so the row can fetch the account number on demand rather
  // than the queue carrying every one of them up front.
  creds: ReviewCreds;
  busy: boolean;
  onMove: (
    id: string,
    status: PayoutStatus,
    extra?: { provider_reference?: string; failure_reason?: string },
  ) => Promise<void>;
}) {
  const [mode, setMode] = useState<"idle" | "paid" | "failed">("idle");
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const { payout } = row;
  const open = payout.status === "pending" || payout.status === "processing";

  return (
    <article className="panel review-row">
      <div className="row-between" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div className="payout-amount">${payout.amount.formatted}</div>
          <div className="log-title">{row.contributor_name ?? row.payout.contributor_id}</div>
          <div className="log-meta muted">
            Requested {formatDate(payout.requested_at)}
            {row.contributor_email ? ` · ${row.contributor_email}` : ""}
          </div>
        </div>
        <div className="log-pills">
          <span className={`pill ${payout.status === "paid" ? "pill-approved" : ""}`}>
            {payout.status}
          </span>
          {row.previous_payouts === 0 && (
            <span className="pill pill-pending">First payout</span>
          )}
          {row.contributor_status !== "active" && (
            <span className="pill pill-rejected">{row.contributor_status}</span>
          )}
        </div>
      </div>

      {/* Where the money is going — the one thing that must be right.
          Masked until somebody asks for it: the queue is a list of people
          to pay, and it should not double as a list of bank accounts on
          anybody's screen or in anybody's screenshot. */}
      <div className="payout-destination">
        <span className="muted" style={{ fontSize: 11 }}>Send to</span>
        {payout.payout_details ? (
          <PayoutDestinationBlock
            creds={creds}
            payoutId={payout.payout_id}
            details={payout.payout_details}
          />
        ) : (
          <div className="mono">{payout.paypal_email || "—"}</div>
        )}
      </div>

      <div className="muted" style={{ fontSize: 12 }}>
        {row.recordings_approved} approved recordings · ${row.lifetime_earned.formatted} earned
        all time · ${row.paid_out.formatted} already paid · ${row.balance.formatted} still
        unredeemed
      </div>

      {payout.provider_reference && (
        <div className="muted" style={{ fontSize: 12 }}>
          Reference: <span className="mono">{payout.provider_reference}</span>
        </div>
      )}
      {payout.failure_reason && <div className="form-error">{payout.failure_reason}</div>}

      {open && mode === "idle" && (
        <div className="btn-row mt-16">
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "auto" }}
            disabled={busy}
            onClick={() => setMode("paid")}
          >
            Mark paid
          </button>
          {payout.status === "pending" && (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => void onMove(payout.payout_id, "processing")}
            >
              Mark in progress
            </button>
          )}
          <button
            type="button"
            className="btn btn-danger-ghost"
            disabled={busy}
            onClick={() => setMode("failed")}
          >
            Mark failed
          </button>
        </div>
      )}

      {mode === "paid" && (
        <div className="review-reason mt-16">
          <div className="field">
            <label htmlFor={`ref-${payout.payout_id}`}>PayPal transaction reference</label>
            <input
              id={`ref-${payout.payout_id}`}
              value={reference}
              placeholder="e.g. 8XY12345AB678901C"
              onChange={(event) => setReference(event.target.value)}
            />
            <div className="hint">
              Recorded against the payout so it can be traced later. Marking paid moves the
              money out of reserved and can't be undone.
            </div>
          </div>
          <div className="btn-row">
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: "auto" }}
              disabled={busy || !reference.trim()}
              onClick={() =>
                void onMove(payout.payout_id, "paid", {
                  provider_reference: reference.trim(),
                })
              }
            >
              {busy ? "Saving…" : `Confirm $${payout.amount.formatted} paid`}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setMode("idle")}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {mode === "failed" && (
        <div className="review-reason mt-16">
          <div className="field">
            <label htmlFor={`why-${payout.payout_id}`}>What went wrong?</label>
            <input
              id={`why-${payout.payout_id}`}
              value={reason}
              placeholder="e.g. PayPal rejected the address"
              onChange={(event) => setReason(event.target.value)}
            />
            <div className="hint">
              The contributor sees this, and the money goes straight back to their balance
              so they can fix it and try again.
            </div>
          </div>
          <div className="btn-row">
            <button
              type="button"
              className="btn btn-danger-ghost"
              disabled={busy || !reason.trim()}
              onClick={() =>
                void onMove(payout.payout_id, "failed", { failure_reason: reason.trim() })
              }
            >
              {busy ? "Saving…" : "Mark failed"}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setMode("idle")}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </article>
  );
}


/**
 * The bank details for one payout.
 *
 * Opens masked and fetches the account number only when somebody says they
 * are about to send money. That request is the only one in the service that
 * decrypts a number, and it is logged against whoever made it — which is
 * only meaningful if it happens when a person actually needs it rather than
 * on every page load.
 */
function PayoutDestinationBlock({
  creds,
  payoutId,
  details,
}: {
  creds: ReviewCreds;
  payoutId: string;
  details: PayoutDetails;
}) {
  const toast = useToast();
  const [full, setFull] = useState<PayoutDestination | null>(null);
  const [loading, setLoading] = useState(false);

  async function reveal() {
    setLoading(true);
    try {
      setFull(await payoutAdmin.destination(creds, payoutId));
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="mono">{details.full_legal_name}</div>
      <div className="mono">
        {details.bank_name} · {details.currency} · {details.country_name ?? details.country}
      </div>
      <div className="mono">
        {full ? full.account_number : `····${details.account_number_last4}`}
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        {details.beneficiary_address}
        {details.bank_address ? ` · Bank: ${details.bank_address}` : ""}
        {details.email ? ` · Wise: ${details.email}` : ""}
      </div>
      {!full && (
        <button
          type="button"
          className="btn btn-ghost btn-sm mt-8"
          disabled={loading}
          onClick={() => void reveal()}
        >
          {loading ? "Fetching…" : "Show account number"}
        </button>
      )}
    </div>
  );
}
